import { h } from 'hyperapp';
import './dropdown.less';
import { scrollTo } from '../../../utils/scroll';
import { isNotDesktop } from '../../../utils/isNotDesktop';

export const ddState = {
    isOpened: false,
    focusedElement: 0,
    selected: 0,
    value: null,
};

export const ddActions = {
    setFocusedElement: value => state => ({ ...state, focusedElement: value }),
    setSelectedElement: value => state => ({
        ...state,
        focusedElement: value,
        selected: value,
        isOpened: false,
    }),
    openDropdown: () => state => ({ ...state, isOpened: true }),
    closeDropdown: () => state => ({
        ...state,
        isOpened: false,
        intentToCloseDD: false,
        focusedElement: state.selected,
    }),
    toggleDropdown: () => state => ({ ...state, isOpened: !state.isOpened }),
    setValue: value => state => ({ ...state, value }),
};

const useOutsideClick = (element, cb) => {
    function handleOutSideClick(event) {
        const includes = element.contains(event.target);
        if (!includes) {
            cb();
        }
    }
    window.addEventListener('click', handleOutSideClick);
    return () => window.removeEventListener('click', handleOutSideClick);
};

const subscriptionMap = new Map();

const keyCodeUpArrow = 38;
const keyCodeDownArrow = 40;
const keyCodeTabKey = 9;

export const Dropdown = ({
    isOpened,
    items,
    focusedElement,
    selected,
    closeDropdown,
    setFocusedElement,
    toggleDropdown,
    setSelectedElement,
    setValue,
    disabled,
    hidden,
    label = '',
    onSelect = () => {},
    value,
    key,
    hide,
}) => (
    <div
        class={`dd__container ${hide ? 'dd_container_state_hide' : ''}`}
        data-type={items[0] ? items[0].categoryTag : ''}
        data-selected={value ? value.categoryTag : (items[0] && items[0].categoryTag) || ''}
    >
        {isNotDesktop() ? (
            <div class="dd__container-select">
                <select
                    key="mobile_dropdown"
                    class="dd_select"
                    oncreate={() => {
                        if (value === null) {
                            setValue(items[0]);
                        }
                    }}
                    disabled={disabled}
                    onchange={event => {
                        const { value: selectedValue } = event.target;
                        const index = items.findIndex(i => i.key === selectedValue);
                        const item = items[index];
                        setSelectedElement(index);
                        setValue(item);
                        onSelect(item);
                    }}
                >
                    {items.map((item, index) => (
                        <option
                            data-item-name={item.categoryTag}
                            key={item.key}
                            value={item.key}
                            data-index={index}
                            selected={selected === index}
                        >
                            {item.title}
                        </option>
                    ))}
                </select>
                <div class="dd__caret" />
            </div>
        ) : (
            <div
                key="desktop_dropdown"
                style={{ display: hidden ? 'none' : '' }}
                oncreate={element => {
                    const unsubscribeFn = useOutsideClick(element, closeDropdown);
                    subscriptionMap.set(`${label}-${key}`, unsubscribeFn);
                    if (value === null) {
                        setValue(items[0]);
                    }
                }}
                data-selected={selected}
                onupdate={(element, oldAttr) => {
                    if (oldAttr['data-selected'] !== selected) {
                        setValue(items[selected]);
                    }
                }}
                ondestroy={() => {
                    const unlisten = subscriptionMap.get(`${label}-${key}`);
                    unlisten();
                    subscriptionMap.delete(`${label}-${key}`);
                }}
            >
                <div
                    class="dd"
                    onkeydown={event => {
                        if (!isOpened) {
                            return;
                        }
                        if (event.keyCode === keyCodeDownArrow) {
                            event.preventDefault();
                            setFocusedElement(Math.min(focusedElement + 1, items.length - 1));
                        }
                        if (event.keyCode === keyCodeUpArrow) {
                            event.preventDefault();
                            setFocusedElement(Math.max(focusedElement - 1, 0));
                        }
                        if (
                            event.keyCode === keyCodeTabKey &&
                            focusedElement === items.length - 1
                        ) {
                            closeDropdown();
                        }
                    }}
                >
                    <button
                        class="dd__button"
                        type="button"
                        disabled={disabled ? 'disabled' : false}
                        aria-expanded={isOpened.toString()}
                        onclick={() => {
                            if (disabled) return;
                            toggleDropdown();
                        }}
                        onupdate={(element, oldAttrs) => {
                            const isClosedAction =
                                oldAttrs['aria-expanded'] === 'true' && isOpened === false;
                            if (isClosedAction) {
                                element.focus();
                            }
                        }}
                    >
                        <div class="dd__button__content">{items[selected].title}</div>
                        <span class={`dd__caret${isOpened ? ' dd__caret_opened' : ''}`} />
                    </button>
                    {isOpened ? (
                        <ul
                            class={`dd__menu${isOpened ? ' dd__menu_visible' : ''}`}
                            role="menu"
                            oncreate={ddMenu => {
                                if (!selected) return;

                                const selectedMenuItem = document.querySelector(
                                    '.dd__item_selected'
                                );
                                const targetCoordinate =
                                    selectedMenuItem.getBoundingClientRect().top -
                                    ddMenu.getBoundingClientRect().top;

                                scrollTo({ element: ddMenu }, targetCoordinate);
                            }}
                        >
                            {items.map((item, index) => (
                                <li key={item.key}>
                                    <a
                                        class={`dd__item${
                                            selected === index ? ' dd__item_selected' : ''
                                        }`}
                                        href="#"
                                        rel="menuitem"
                                        data-item-name={item.categoryTag}
                                        onclick={event => {
                                            event.preventDefault();
                                            setSelectedElement(index);
                                            setValue(item);
                                            onSelect(item);
                                        }}
                                        onupdate={element => {
                                            if (index === focusedElement && isOpened) {
                                                element.focus();
                                            }
                                        }}
                                        onfocus={() => {
                                            setFocusedElement(index);
                                        }}
                                    >
                                        {item.title}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    ) : null}
                </div>
            </div>
        )}
    </div>
);
